import { Component } from 'react';
import { ScheduleI } from '~src/interfaces/schedule.interface';
import { SurveyUseCase } from '~src/interfaces/surveys.interfaces';
import PrimaryButton from '~src/shared/buttons/basicButtons/PrimaryButton.sc';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import Checkbox from '~src/shared/form/Checkbox';

import ToggleBox from '~src/shared/form/toggleBox/ToggleBox';
import SvgIcon from '~src/shared/SvgIcon';
import { SurveyScopes } from '~src/modules/surveys/interfaces';

import {
  ChipDescription,
  LabelContainer,
  ModalButtonSectionContainer,
  ModalHeader,
  ModalSectionContainer,
  ModalSubHeader,
  PeopleSectionHeader,
  SecondPeopleSectionBottom,
  SelectionSubtitle,
  SelectionWrapper,
  TitleContainer,
  ToggleWrapper,
} from './styles/SectionStyles';
import { KeyTextObj } from '~src/interfaces/translation.interface';
import HoverInfoIcon from '~src/shared/tooltips/HoverInfoIcon';
import PeopleChips from './customInputs/PeopleChips';
import { CancelToken } from 'axios';
import { Segment, SegmentType } from '~src/interfaces/segment.interface';
import { SessionUserI } from '~src/interfaces/session.types';
import {TFunction} from "i18next";

export const getModalTexts = (t: TFunction): ModalTexts => ({
  header: {
    key: 'x.surveys.schedule.asksection.togglebox.modal.header',
    txt: t('x.surveys.schedule.asksection.togglebox.modal.header'),
  },
  title: {
    key: 'x.surveys.schedule.asksection.togglebox.modal.title',
    txt: t('x.surveys.schedule.asksection.togglebox.modal.title'),
  },
  subtitle: {
    key: 'x.surveys.schedule.asksection.togglebox.modal.subtitle',
    txt: t('x.surveys.schedule.asksection.togglebox.modal.subtitle'),
  },
  submit: {
    key: 'x.surveys.schedule.asksection.togglebox.modal.submit',
    txt: t('x.surveys.schedule.asksection.togglebox.modal.submit'),
  },
  cancel: {
    key: 'x.surveys.schedule.asksection.togglebox.modal.cancel',
    txt: t('x.surveys.schedule.asksection.togglebox.modal.cancel'),
  },
});

export interface ModalTexts {
  header: KeyTextObj;
  title: KeyTextObj;
  subtitle: KeyTextObj;
  submit: KeyTextObj;
  cancel: KeyTextObj;
}

export const modalBody = (onSubmit: () => void, onCancel: () => void, modalTexts: ModalTexts) => (
  <span>
    <ModalHeader>
      <ModalSectionContainer>
        <SvgIcon name="iconDanger" width={60} />
      </ModalSectionContainer>
      <ModalSectionContainer data-translation={modalTexts.title.key}>
        {modalTexts.title.txt}
      </ModalSectionContainer>
    </ModalHeader>
    <ModalSectionContainer>
      <ModalSubHeader data-translation={modalTexts.subtitle.key}>{modalTexts.subtitle.txt}</ModalSubHeader>
    </ModalSectionContainer>
    <ModalSectionContainer>
      <ModalButtonSectionContainer>
        <SecondaryButton onClick={onCancel}>
          <span data-translation={modalTexts.cancel.key}>{modalTexts.cancel.txt}</span>
        </SecondaryButton>
        <PrimaryButton onClick={onSubmit}>
          <span data-translation={modalTexts.submit.key}>{modalTexts.submit.txt}</span>
        </PrimaryButton>
      </ModalButtonSectionContainer>
    </ModalSectionContainer>
  </span>
);

interface Props {
  t: TFunction;
  schedule: ScheduleI;
  oldSchedule: ScheduleI;
  onChangeToggleBox: (active: boolean) => void;
  onChangeManagerAnswerVisible?: (isVisible: boolean) => void;
  title: string;
  active: boolean;
  changeFlag: (value: boolean, key: keyof ScheduleI) => void;
  subject: string;
  useCase: SurveyUseCase;
  scope?: SurveyScopes;
  fetchSearchSegments: (
    value: string,
    cancelToken: CancelToken,
    types?: SegmentType[],
    includeDeactivated?: boolean,
  ) => Promise<Segment[]>;
  getAskedPeople: (people: Segment[], key: string) => void;
  sessionUser?: SessionUserI;
}

class ToggleBoxSection extends Component<Props> {
  static defaultProps = {
    active: false,
    useCase: null,
  };

  changeSelfSelection = (value: boolean) => {
    this.props.changeFlag(value, 'selfSelection');
  };

  changeManagersSelection = (value: boolean) => {
    this.props.changeFlag(value, 'managersSelection');
  };

  changeAskManagersOfAbouts = (value: boolean): void => {
    this.props.changeFlag(value, 'askManagersOfAbouts');
  };

  changeAskTeamMembersOfAbouts = (value: boolean): void => {
    this.props.changeFlag(value, 'askTeamMembersOfAbouts');
  };

  render() {
    const {
      t,
      title,
      active,
      onChangeToggleBox,
      onChangeManagerAnswerVisible,
      schedule,
      useCase,
      oldSchedule,
    } = this.props;
    return (
      <PeopleSectionHeader>
        <div>
          <SecondPeopleSectionBottom>
            <SvgIcon name="iconAnonymous" />
            <TitleContainer>{title}</TitleContainer>
            <ToggleWrapper>
              <ToggleBox
                modalTexts={getModalTexts(t)}
                renderModelBody={modalBody}
                showModalOnChange={!oldSchedule.anonymous && oldSchedule.wasStarted}
                locked={oldSchedule.anonymous && oldSchedule.wasStarted}
                active={active}
                onChangeBox={onChangeToggleBox}
                title={[
                  t('x.surveys.schedule.asksection.togglebox.yes'),
                  t('x.surveys.schedule.asksection.togglebox.no'),
                ]}
                popoverMessage={{
                  key: 'x.surveys.schedule.asksection.togglebox.locked_message',
                  txt: t('x.surveys.schedule.asksection.togglebox.locked_message'),
                }}
              />
            </ToggleWrapper>
          </SecondPeopleSectionBottom>
          {this.props.useCase === SurveyUseCase.ThreeSixty && active && (
            <SecondPeopleSectionBottom>
              <SvgIcon name="iconAnonymous" />
              <TitleContainer>
                {t('x.surveys.schedule.asksection.checkbox.show_manager_rating')}&nbsp;
                <HoverInfoIcon
                  infoText={{
                    key: 'x.surveys.schedule.asksection.checkbox.show_manager_rating.info',
                    txt: t('x.surveys.schedule.asksection.checkbox.show_manager_rating.info'),
                  }}
                />
              </TitleContainer>
              <ToggleBox
                active={schedule.isAnonymousManagerAnswerVisible}
                onChangeBox={onChangeManagerAnswerVisible ? onChangeManagerAnswerVisible : () => null}
                title={[
                  t('x.surveys.schedule.asksection.togglebox.yes'),
                  t('x.surveys.schedule.asksection.togglebox.no'),
                ]}
              />
            </SecondPeopleSectionBottom>
          )}
        </div>
        {this.props.subject === 'people' && (
          <SelectionWrapper>
            <Checkbox
              id="managers_selection"
              label={t(
                `x.surveys.schedule.asksection.checkbox.managers_selection.${useCase}.${this.props.scope}`,
              )}
              labelKey={`x.surveys.schedule.asksection.checkbox.managers_selection.${useCase}.${this.props.scope}`}
              isChecked={schedule.managersSelection}
              onChange={this.changeManagersSelection}
            />
            <SelectionSubtitle
              /*  tslint:disable max-line-length */
              data-translation={`x.surveys.schedule.asksection.checkbox.managers_selection.subtitle.${useCase}.${this.props.scope}`}
            >
              {t(
                `x.surveys.schedule.asksection.checkbox.managers_selection.subtitle.${useCase}.${this.props.scope}`,
              )}
            </SelectionSubtitle>
            <Checkbox
              id="self_selection"
              label={t(
                `x.surveys.schedule.asksection.checkbox.self_selection.${useCase}.${this.props.scope}`,
              )}
              labelKey={`x.surveys.schedule.asksection.checkbox.self_selection.${useCase}.${this.props.scope}`}
              isChecked={schedule.selfSelection}
              onChange={this.changeSelfSelection}
            />
            <SelectionSubtitle
              data-translation={`x.surveys.schedule.asksection.checkbox.self_selection.subtitle.${useCase}.${this.props.scope}`}
            >
              {t(
                `x.surveys.schedule.asksection.checkbox.self_selection.subtitle.${useCase}.${this.props.scope}`,
              )}
            </SelectionSubtitle>
            <LabelContainer>
              <ChipDescription
                className="label"
                data-translation={t('x.surveys.schedule.input.ask.custom_selection')}
              >
                <>{t('x.surveys.schedule.input.ask.custom_selection')}</>
                <SelectionSubtitle
                  style={{ marginLeft: 0, marginBottom: '0.25rem' }}
                  data-translation={'x.surveys.schedule.input.ask.custom_selector.description'}
                >
                  {t('x.surveys.schedule.input.ask.custom_selector.description')}
                </SelectionSubtitle>
              </ChipDescription>

              <PeopleChips
                placeHolderKey="x.surveys.schedule.input.ask.custom_selector_placeholder"
                fetchSuggestions={this.props.fetchSearchSegments}
                chipsName="customSelectors"
                excludeSegmentIds={[
                  ...this.props.schedule.about.map((item) => item.id),
                ]}
                selectedPeople={this.props.schedule.customSelectors || []}
                onChangePeople={this.props.getAskedPeople}
              />
            </LabelContainer>
          </SelectionWrapper>
        )}
        {this.props.useCase === SurveyUseCase.ThreeSixty && (
          <SelectionWrapper>
            <Checkbox
              id="ask_managers_of_abouts"
              label={t(
                `x.surveys.schedule.asksection.checkbox.ask_managers_of_abouts.${useCase}.${this.props.scope}`,
              )}
              labelKey={`x.surveys.schedule.asksection.checkbox.ask_managers_of_abouts.${useCase}.${this.props.scope}`}
              isChecked={schedule.askManagersOfAbouts}
              onChange={this.changeAskManagersOfAbouts}
            />
            <SelectionSubtitle
              /*  tslint:disable max-line-length */
              data-translation={`x.surveys.schedule.asksection.checkbox.ask_managers_of_abouts.subtitle.${useCase}.${this.props.scope}`}
            >
              {t(
                `x.surveys.schedule.asksection.checkbox.ask_managers_of_abouts.subtitle.${useCase}.${this.props.scope}`,
              )}
            </SelectionSubtitle>

            <Checkbox
              id="ask_team_members_of_abouts"
              label={t(
                `x.surveys.schedule.asksection.checkbox.ask_team_members_of_abouts.${useCase}.${this.props.scope}`,
              )}
              labelKey={`x.surveys.schedule.asksection.checkbox.ask_team_members_of_abouts.${useCase}.${this.props.scope}`}
              isChecked={schedule.askTeamMembersOfAbouts}
              onChange={this.changeAskTeamMembersOfAbouts}
            />
            <SelectionSubtitle
              /*  tslint:disable max-line-length */
              data-translation={`x.surveys.schedule.asksection.checkbox.ask_team_members_of_abouts.subtitle.${useCase}.${this.props.scope}`}
            >
              {t(
                `x.surveys.schedule.asksection.checkbox.ask_team_members_of_abouts.subtitle.${useCase}.${this.props.scope}`,
              )}
            </SelectionSubtitle>
          </SelectionWrapper>
        )}
      </PeopleSectionHeader>
    );
  }
}

export default ToggleBoxSection;
